/**
 * Attachment requests (F5b).
 *
 * The upload sends `FormData`, and the content type is deliberately NOT set:
 * a multipart body needs a boundary parameter that only the browser can
 * generate, and setting `Content-Type: multipart/form-data` by hand omits it
 * and makes every upload unparseable. `api-client` already skips its JSON
 * header for a `FormData` body for exactly this reason.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10
 */
import type {
  AttachmentSummary,
  DownloadLink,
  ListAttachmentsQuery,
  UpdateAttachmentRequest,
} from '@nexus/contracts';

import { api } from './api-client';
import type { PageMeta } from './pagination';

/** A page of attachments. */
export interface AttachmentPage {
  data: AttachmentSummary[];
  meta: PageMeta;
}

/** What an upload needs beside the file. */
export interface UploadFields {
  type: string;
  confidentiality: string;
  startupId?: string;
  pilotId?: string;
}

export async function listAttachments(
  query: ListAttachmentsQuery,
): Promise<AttachmentPage> {
  return api.get<AttachmentPage>('/attachments', { query });
}

export async function uploadAttachment(
  file: File,
  fields: UploadFields,
): Promise<AttachmentSummary> {
  const form = new FormData();
  form.append('file', file);

  /* Appended individually rather than as JSON: the endpoint reads them as
     multipart text fields, which is what a form post produces.

     Listed explicitly rather than looped over `Object.entries`, which widens
     every value to `any` and would let a non-string through unnoticed. */
  form.append('type', fields.type);
  form.append('confidentiality', fields.confidentiality);
  if (fields.startupId !== undefined) form.append('startupId', fields.startupId);
  if (fields.pilotId !== undefined) form.append('pilotId', fields.pilotId);

  return api.post<AttachmentSummary>('/attachments', form);
}

export async function updateAttachment(
  id: string,
  body: UpdateAttachmentRequest,
): Promise<AttachmentSummary> {
  return api.patch<AttachmentSummary>(`/attachments/${id}`, body);
}

export async function deleteAttachment(id: string, version: number): Promise<void> {
  /* `undefined`, not `void`: the endpoint answers 204 with no body, and
     `void` is only valid as a return type — not as a type argument. */
  await api.delete<undefined>(`/attachments/${id}`, { query: { version } });
}

/**
 * Asks for a signed download link.
 *
 * A POST, not a GET, because it has an effect: PRD §5.10 requires the
 * download logged, and this is where that happens.
 */
export async function createDownloadLink(id: string): Promise<DownloadLink> {
  return api.post<DownloadLink>(`/attachments/${id}/download-link`);
}
